// ===== Catálogo de produtos — os do banco =====
//
// Issues #15 e #4 do Kaio (23/09). Até aqui esta tela lia a constante vazia
// `PRODUTOS` de data.jsx, e não os 12.723 produtos do banco. Por isso a base
// aparecia zerada. O resto era do protótipo: mais vendidos, encalhados,
// estoque, custo e uma "importação de lista" que sumia no F5. Saiu tudo que
// não tem dado real por trás.
//
// Edição: só descrição técnica, dimensões e peso. É o que a migration 0070
// concede por coluna, a quem tem `produtos.edit` na matriz (admin, diretor,
// backoffice, produção). O resto vem do Nomus e é só leitura.
// Spec: docs/superpowers/specs/2026-09-23-catalogo-de-produtos-e-quem-edita.md
import React from 'react';
import { Icon } from './icons.jsx';
import { useStore } from './store.jsx';
import { Select } from './modals.jsx';
import { usePermission } from './auth.jsx';
import { buscarProdutos } from './busca-produtos.js';

const { useState: useStateProd, useMemo: useMemoProd } = React;

// 15 mil linhas não cabem numa renderização só sem travar a tela. A busca e o
// filtro rodam sobre todas; a lista mostra 50 e o "Mostrar mais" soma outras 50.
const POR_VEZ = 50;

// "0,35" e "0.35" são o mesmo peso. Vírgula é decimal; com vírgula, ponto é milhar.
function lerNumeroBR(texto) {
  const s = String(texto ?? '').trim();
  if (!s) return null;
  const n = Number(s.includes(',') ? s.replace(/\./g, '').replace(',', '.') : s);
  return Number.isFinite(n) ? n : null;
}

const ORIGENS = { 0: 'Nacional', 1: 'Estrangeira (importação direta)', 2: 'Estrangeira (mercado interno)' };

function ProdutosPage({ onOpenProduto }) {
  const store = useStore();
  const produtos = store.produtos || [];
  // Cada tecla em `q` refiltra 15 mil produtos (55 a 142 ms medidos); esperar 250 ms depois da última tecla junta "per" numa só
  // filtragem. `texto` é o que aparece no campo; `q` só muda quando a espera acaba. Mesmo molde de src/clientes.jsx.
  const [texto, setTexto] = useStateProd('');
  const [q, setQ] = useStateProd('');
  const espera = React.useRef(null);
  const digitar = (valor) => {
    setTexto(valor);
    clearTimeout(espera.current);
    espera.current = setTimeout(() => setQ(valor), 250);
  };
  React.useEffect(() => () => clearTimeout(espera.current), []);
  const [grupo, setGrupo] = useStateProd('all');
  const [soSemDescricao, setSoSemDescricao] = useStateProd(false);
  const [limite, setLimite] = useStateProd(POR_VEZ);
  const chaveDosFiltros = JSON.stringify([q, grupo, soSemDescricao]);
  const [chaveAnterior, setChaveAnterior] = useStateProd(chaveDosFiltros);
  if (chaveAnterior !== chaveDosFiltros) {
    // Ajuste durante a renderização (padrão documentado do React): evita um quadro com a lista nova ainda no limite antigo.
    setChaveAnterior(chaveDosFiltros);
    setLimite(POR_VEZ);
  }

  const grupos = useMemoProd(() => [...new Set(produtos.map((p) => p.grupo).filter(Boolean))].sort(), [produtos]);
  const semDescricao = useMemoProd(() => produtos.filter((p) => !p.descricaoEstendida).length, [produtos]);

  const filtrados = useMemoProd(() => {
    const base = produtos.filter((p) => {
      if (grupo !== 'all' && p.grupo !== grupo) return false;
      if (soSemDescricao && p.descricaoEstendida) return false;
      return true;
    });
    return q.trim() ? buscarProdutos(base, q, { limite: Infinity }) : base;
  }, [produtos, q, grupo, soSemDescricao]);

  const visiveis = filtrados.slice(0, limite);
  const filtrando = texto || q || grupo !== 'all' || soSemDescricao;
  const limpar = () => {
    // Uma digitação pendente não pode voltar depois de limpar.
    clearTimeout(espera.current);
    setTexto(''); setQ(''); setGrupo('all'); setSoSemDescricao(false);
  };

  return (
    <div className="main main-scroll dash produtos-page" data-screen-label="Produtos">
      <div className="dash-hero">
        <div>
          <div className="dash-hero-greet">Catálogo de <strong>produtos</strong></div>
          <div className="dash-hero-date">{produtos.length.toLocaleString('pt-BR')} produtos vindos do Nomus</div>
        </div>
      </div>

      {/* Faixa própria: o KPIStat (compartilhado com o painel) imprime o número cru, sem ponto de milhar. */}
      <div className="prod-faixa">
        <button type="button" className="prod-faixa-item" onClick={limpar}>
          <span className="prod-faixa-num">{produtos.length.toLocaleString('pt-BR')}</span>
          <span className="prod-faixa-rot">produtos</span>
        </button>
        <button type="button" className={`prod-faixa-item${semDescricao > 0 ? ' warn' : ''}`} aria-pressed={soSemDescricao}
          onClick={() => setSoSemDescricao(true)}>
          <span className="prod-faixa-num">{semDescricao.toLocaleString('pt-BR')}</span>
          <span className="prod-faixa-rot">sem descrição técnica</span>
        </button>
      </div>

      <div className="card-section">
        <div className="dash-card-head" style={{ flexWrap: 'wrap', gap: 8 }}>
          <div className="title"><Icon name="package" size={14} /> Catálogo</div>
          <div className="sub"><strong>{filtrados.length.toLocaleString('pt-BR')}</strong> produto{filtrados.length !== 1 ? 's' : ''}</div>
          <div className="prod-busca" style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '0 10px', border: '1px solid var(--color-border)', borderRadius: 'var(--radius-sm)', height: 32, minWidth: 240, background: 'var(--color-surface)' }}>
            <Icon name="search" size={13} style={{ color: 'var(--color-ink-soft)' }} />
            <input placeholder="Código, nome ou descrição" value={texto} onChange={(e) => digitar(e.target.value)}
              style={{ border: 0, outline: 'none', flex: 1, fontSize: 12.5, background: 'transparent' }} />
          </div>
        </div>
        <div className="cat-filterbar">
          <Select variant="chip" prefix="Grupo" value={grupo} onChange={setGrupo}
            options={[{ value: 'all', label: 'Todos os grupos' }, ...grupos.map((g) => ({ value: g, label: g }))]} />
          <button className={`chip-btn ${soSemDescricao ? 'on' : ''}`} onClick={() => setSoSemDescricao((v) => !v)} aria-pressed={soSemDescricao}>
            <Icon name="file-text" size={12} /> Só sem descrição técnica
          </button>
          {filtrando && (
            <button className="chip-btn" style={{ marginLeft: 'auto' }} onClick={limpar}>
              <Icon name="x" size={12} className="x" /> Limpar filtros
            </button>
          )}
        </div>

        <div>
          {visiveis.map((p) => {
            // Código igual ao nome: mostrar o texto uma vez e usar a descrição do Nomus como segunda informação.
            const iguais = !!p.codigo && String(p.codigo).trim().toLowerCase() === String(p.nome ?? '').trim().toLowerCase();
            return (
              <button key={p.id} className={`prod-linha${iguais ? ' igual' : ''}`} onClick={() => onOpenProduto && onOpenProduto(p.id)}>
                <span className="prod-cod mono">{p.codigo || '—'}</span>
                <span className="prod-nome">{p.nome}</span>
                {iguais && <span className={`prod-desc${p.descricao ? '' : ' vazia'}`}>{p.descricao || '—'}</span>}
                <span className="prod-grupo">{p.grupo || '—'}</span>
                <span className={`prod-sit ${p.descricaoEstendida ? 'ok' : 'warn'}`}>
                  {p.descricaoEstendida ? 'com descrição técnica' : 'sem descrição técnica'}
                </span>
              </button>
            );
          })}
          {filtrados.length > POR_VEZ && (
            <div className="prod-mais" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 12, padding: '14px 0', fontSize: 12, color: 'var(--color-ink-soft)' }}>
              <span>Mostrando {Math.min(limite, filtrados.length).toLocaleString('pt-BR')} de {filtrados.length.toLocaleString('pt-BR')} produtos</span>
              {limite < filtrados.length && <button className="btn" onClick={() => setLimite((l) => l + POR_VEZ)}>Mostrar mais (+{POR_VEZ})</button>}
            </div>
          )}
          {filtrados.length === 0 && (
            <div className="empty" style={{ padding: 40, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10 }}>
              {produtos.length === 0 ? 'O catálogo ainda não carregou.' : 'Nenhum produto bate com os filtros.'}
              {filtrando && <button className="btn sm" onClick={limpar}><Icon name="x" size={12} /> Limpar filtros</button>}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function Campo({ rotulo, children }) {
  return (
    <div className="ficha-stat"><div className="lbl">{rotulo}</div><div className="val">{children}</div></div>
  );
}

function ProdutoDrawer({ produtoId, onClose }) {
  const store = useStore();
  const podeEditar = usePermission('produtos.edit');
  const produto = (store.produtos || []).find((p) => p.id === produtoId) || null;
  const [editando, setEditando] = useStateProd(false);
  const [form, setForm] = useStateProd({ descricaoEstendida: '', dimensoes: '', peso: '' });
  const [salvando, setSalvando] = useStateProd(false);
  if (!produto) return null;

  const abrirEdicao = () => {
    setForm({
      descricaoEstendida: produto.descricaoEstendida || '',
      dimensoes: produto.dimensoes || '',
      peso: produto.pesoKg != null ? String(produto.pesoKg).replace('.', ',') : '',
    });
    setEditando(true);
  };
  const salvar = async () => {
    setSalvando(true);
    const ok = await store.atualizarProduto(produto.id, {
      descricaoEstendida: form.descricaoEstendida,
      dimensoes: form.dimensoes,
      pesoKg: lerNumeroBR(form.peso),
    });
    setSalvando(false);
    if (ok) setEditando(false);
  };
  const unidade = produto.idUnidadeMedida === 2 ? 'Metro' : 'Unidade';
  const peso = produto.pesoKg != null ? `${String(produto.pesoKg).replace('.', ',')} kg` : '—';

  return (
    <aside className="drawer drawer-cli drawer-produto" data-screen-label={`Produto / ${produto.nome}`}>
      <div className="drawer-head">
        <div className="top">
          <div className="prod-ic" style={{ width: 44, height: 44 }}><Icon name="package" size={20} /></div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div className="drawer-eyebrow">
              <span className="mono">{produto.codigo || '—'}</span>
              <span className="sep">·</span>
              <span>{produto.grupo || '—'}</span>
            </div>
            <div className="title">{produto.nome}</div>
          </div>
          <button className="icon-btn" onClick={onClose} aria-label="Fechar"><Icon name="x" size={16} /></button>
        </div>
      </div>
      <div className="drawer-body">
        <div className="section-h">Do Nomus · só leitura</div>
        <div className="ficha-strip" style={{ gridTemplateColumns: 'repeat(2, 1fr)' }}>
          <Campo rotulo="idProduto"><span className="mono">{produto.idProduto || 'sem id do Nomus'}</span></Campo>
          <Campo rotulo="Unidade">{unidade}</Campo>
          <Campo rotulo="NCM"><span className="mono">{produto.ncm || '—'}</span></Campo>
          <Campo rotulo="Origem">{ORIGENS[produto.origemProduto] ?? (produto.origemProduto ?? '—')}</Campo>
        </div>
        {produto.descricao && (
          <div style={{ fontSize: 12, color: 'var(--color-ink-soft)', marginTop: 8, lineHeight: 1.5 }}>{produto.descricao}</div>
        )}

        <div className="section-h" style={{ marginTop: 16, display: 'flex', alignItems: 'center', gap: 8 }}>
          Do CRM · vai na proposta
          {podeEditar && !editando && (
            <button className="btn sm" onClick={abrirEdicao} style={{ fontSize: 11 }}><Icon name="pencil" size={11} /> Editar</button>
          )}
        </div>

        {editando ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <label style={{ fontSize: 11.5, color: 'var(--color-ink-soft)' }}>Descrição técnica
              <textarea value={form.descricaoEstendida} onChange={(e) => setForm((f) => ({ ...f, descricaoEstendida: e.target.value }))} autoFocus
                style={{ display: 'block', width: '100%', minHeight: 110, resize: 'vertical', border: '1px solid var(--color-accent)', borderRadius: 'var(--radius-sm)', padding: '8px 10px', fontSize: 12.5, fontFamily: 'inherit', lineHeight: 1.5, marginTop: 4 }}
                placeholder="Materiais, acabamento, potência, proteção… é o texto que sai no documento da proposta." />
            </label>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 120px', gap: 10 }}>
              <label style={{ fontSize: 11.5, color: 'var(--color-ink-soft)' }}>Dimensões
                <input className="input" value={form.dimensoes} onChange={(e) => setForm((f) => ({ ...f, dimensoes: e.target.value }))} placeholder="Ex.: 120 x 40 mm" style={{ display: 'block', width: '100%', marginTop: 4 }} />
              </label>
              <label style={{ fontSize: 11.5, color: 'var(--color-ink-soft)' }}>Peso (kg)
                <input className="input" value={form.peso} onChange={(e) => setForm((f) => ({ ...f, peso: e.target.value }))} inputMode="decimal" placeholder="0,35" style={{ display: 'block', width: '100%', marginTop: 4 }} />
              </label>
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <button className="btn primary sm" onClick={salvar} disabled={salvando}><Icon name="check" size={12} /> {salvando ? 'Salvando…' : 'Salvar'}</button>
              <button className="btn sm" onClick={() => setEditando(false)} disabled={salvando}>Cancelar</button>
            </div>
          </div>
        ) : (
          <>
            <div style={{ fontSize: 12.5, lineHeight: 1.55, padding: '6px 0', color: produto.descricaoEstendida ? 'var(--color-ink)' : 'var(--color-ink-faint)', whiteSpace: 'pre-wrap' }}>
              {produto.descricaoEstendida || <em>Sem descrição técnica. A proposta sai sem ela.</em>}
            </div>
            <div className="ficha-strip" style={{ gridTemplateColumns: 'repeat(2, 1fr)' }}>
              <Campo rotulo="Dimensões">{produto.dimensoes || '—'}</Campo>
              <Campo rotulo="Peso">{peso}</Campo>
            </div>
            {!podeEditar && (
              <div style={{ fontSize: 11.5, color: 'var(--color-ink-soft)', marginTop: 8 }}>
                Seu cargo pode consultar, mas não editar produtos.
              </div>
            )}
          </>
        )}
      </div>
    </aside>
  );
}

export { ProdutosPage, ProdutoDrawer, lerNumeroBR };
